import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Shield, Buildings, UploadSimple, X, FileText, ArrowLeft, PaperPlaneTilt, MagnifyingGlass, CheckCircle, Eye } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { titleCase } from "@/lib/textFormat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCls =
  "h-9 px-2.5 text-sm rounded-sm border border-[#D0D5DD] text-[#101828] focus:outline-none focus:border-[#004B87] focus:ring-1 focus:ring-[#004B87] w-full";
const labelCls = "font-heading text-xs font-bold text-[#475467] uppercase tracking-wide block mb-1";
const sectionCls = "bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3";
const sectionTitleCls = "font-heading text-sm font-bold text-[#1D2939] flex items-center gap-2 pb-2 border-b border-[#EAECF0]";

const STATUS_LABELS = {
  awaiting_supplier_fill: "Awaiting Supplier",
  awaiting_pin_verification: "Awaiting PIN",
  pending: "Pending Admin Review",
  approved: "Approved",
  rejected: "Rejected",
};

// Feb 2026, user's explicit ask: fixed category list instead of free
// text ("give a category dropdown... for now" - more may be added
// later, kept as a simple shared list rather than a separate lookup
// table since it's not expected to change often).
const SUPPLIER_CATEGORIES = ["Raw Material", "Packaging", "Hardware", "Consumable", "Plastic", "Capital", "Die & Tools"];

const emptyForm = {
  company_name: "", is_gst_registered: true, gstin: "", pan: "",
  contact_name: "", contact_email: "", contact_phone: "", secondary_contact_name: "", secondary_phone: "",
  address_line: "", address_line_2: "", city: "", state: "", pincode: "",
  category: "", currency: "INR", supplier_id: "",
  bank_account_number: "", bank_ifsc: "", bank_account_holder_name: "",
  bank_details_confirmed: false, bank_cheque_uploaded: false,
};

// Backend errors are either a plain string, or a {field: [messages]} dict
// (field-level validation) - never render the dict object itself as a
// toast description, React throws on that.
const errMsg = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback || err.message;
  if (typeof detail === "string") return detail;
  return Object.values(detail).flat().join(", ");
};

export default function SupplierOnboardingRequestPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [gstChecking, setGstChecking] = useState(false);
  const [gstResult, setGstResult] = useState(null);
  const [panChecking, setPanChecking] = useState(false);
  const [panResult, setPanResult] = useState(null);

  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [sendLinkOpen, setSendLinkOpen] = useState(false);
  const [supplierEmailInput, setSupplierEmailInput] = useState("");
  const [sendingLink, setSendingLink] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [readingCheque, setReadingCheque] = useState(false);
  const [attachmentsTarget, setAttachmentsTarget] = useState(null);

  // Feb 2026, user's explicit ask: a supplier ("Arpit Steel") got
  // submitted with no check that the typed contact_email was reachable.
  // Tracks the EXACT value that was last verified - editing the field
  // after verifying must require re-verification (verified stays true
  // only while it still matches). Phone OTP (Twilio) was added the
  // same way but the user then asked to drop the requirement for now
  // ("non availability of api" - Twilio credentials were skipped) -
  // removed from here; the backend endpoints are left in place, ready
  // to re-enable this same way once Twilio is configured.
  const [emailOtp, setEmailOtp] = useState({ sentTo: null, verifiedValue: null, code: "", sending: false, verifying: false });
  // Feb 2026, user's explicit ask: "Skip Email OTP for only one
  // supplier... only one in the preview" - mirrors the same one-off
  // allowlisted email the backend accepts without a real OTP (see
  // post_supplier_onboarding_request's docstring) so the Submit button
  // isn't stuck disabled for this one test. Remove once the test is done.
  const EMAIL_OTP_TEST_BYPASS = "arpitsteel222@gmail.com";
  const emailVerified = (form.contact_email || "").trim().toLowerCase() === EMAIL_OTP_TEST_BYPASS
    || (emailOtp.verifiedValue && emailOtp.verifiedValue === form.contact_email);

  const sendEmailOtp = async () => {
    if (!form.contact_email) return;
    setEmailOtp((s) => ({ ...s, sending: true }));
    try {
      await axios.post(`${API}/supplier-onboarding/verify/email/send`, { email: form.contact_email });
      toast.success("Verification code sent", { description: `Check the inbox for ${form.contact_email}` });
      setEmailOtp((s) => ({ ...s, sentTo: form.contact_email, verifiedValue: null, code: "", sending: false }));
    } catch (err) {
      toast.error("Could not send the code", { description: errMsg(err) });
      setEmailOtp((s) => ({ ...s, sending: false }));
    }
  };

  const confirmEmailOtp = async () => {
    setEmailOtp((s) => ({ ...s, verifying: true }));
    try {
      await axios.post(`${API}/supplier-onboarding/verify/email/confirm`, { email: form.contact_email, code: emailOtp.code });
      toast.success("Email verified");
      setEmailOtp((s) => ({ ...s, verifiedValue: form.contact_email, verifying: false }));
    } catch (err) {
      toast.error("Incorrect or expired code", { description: errMsg(err) });
      setEmailOtp((s) => ({ ...s, verifying: false }));
    }
  };

  // Feb 2026, user's explicit ask ("could u use it for mobile
  // verification for the supplier") - phone OTP is back (sent over
  // WhatsApp via chatway.in instead of the never-configured Twilio),
  // same required-before-submit pattern as email above.
  const [phoneOtp, setPhoneOtp] = useState({ sentTo: null, verifiedValue: null, code: "", sending: false, verifying: false });
  const phoneVerified = phoneOtp.verifiedValue && phoneOtp.verifiedValue === form.contact_phone;

  const sendPhoneOtp = async () => {
    if (!form.contact_phone) return;
    setPhoneOtp((s) => ({ ...s, sending: true }));
    try {
      await axios.post(`${API}/supplier-onboarding/verify/phone/send`, { phone: form.contact_phone });
      toast.success("Verification code sent on WhatsApp", { description: `Check WhatsApp on ${form.contact_phone}` });
      setPhoneOtp((s) => ({ ...s, sentTo: form.contact_phone, verifiedValue: null, code: "", sending: false }));
    } catch (err) {
      toast.error("Could not send the code", { description: errMsg(err) });
      setPhoneOtp((s) => ({ ...s, sending: false }));
    }
  };

  const confirmPhoneOtp = async () => {
    setPhoneOtp((s) => ({ ...s, verifying: true }));
    try {
      await axios.post(`${API}/supplier-onboarding/verify/phone/confirm`, { phone: form.contact_phone, code: phoneOtp.code });
      toast.success("Phone verified");
      setPhoneOtp((s) => ({ ...s, verifiedValue: form.contact_phone, verifying: false }));
    } catch (err) {
      toast.error("Incorrect or expired code", { description: errMsg(err) });
      setPhoneOtp((s) => ({ ...s, verifying: false }));
    }
  };

  const loadMyRequests = async () => {
    setLoadingRequests(true);
    try {
      const { data } = await axios.get(`${API}/supplier-onboarding-requests`);
      setMyRequests(data || []);
    } catch (err) {
      // non-fatal - the form above still works even if this list fails to load
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    loadMyRequests();
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setTitleCaseField = (key, value) => setForm((f) => ({ ...f, [key]: titleCase(value) }));
  // Any edit to a bank field (manual typing, not the cheque AI fill
  // itself) invalidates a prior confirmation - the requester must
  // re-tick the checkbox after changing what they're confirming.
  const setBankField = (key, value) => setForm((f) => ({ ...f, [key]: value, bank_details_confirmed: false }));

  const addFiles = (fileList) => {
    setFiles((prev) => [...prev, ...Array.from(fileList)]);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  // Feb 2026, user's explicit ask (raised earlier, not yet done): "we
  // need attached document view for confirmation" - these are still
  // plain in-browser File objects (nothing is uploaded until Submit),
  // so no backend round trip is needed at all - just opens the file
  // straight from memory in a new tab so the requester can double-
  // check it's the right document before submitting.
  const viewFile = (file) => window.open(URL.createObjectURL(file), "_blank");

  const checkGstin = async () => {
    if (!form.gstin) return;
    setGstChecking(true);
    setGstResult(null);
    try {
      const { data } = await axios.post(`${API}/gstin/verify`, { gstin: form.gstin });
      setGstResult({ ok: true, ...data });
    } catch (err) {
      setGstResult({ ok: false, message: errMsg(err, "Could not verify this GSTIN") });
    } finally {
      setGstChecking(false);
    }
  };

  // Feb 2026, user's explicit ask ("can u do PAN verification?") - same
  // gstinapi.in provider/key as GSTIN, but their own /v1/pan/{pan}/verify
  // (NOT iServeU's PAN API, which needs Name+Father's Name+DOB built for
  // individual PAN cards - these are company PANs). name_match_score
  // compares against the typed company name, purely informational.
  const checkPan = async () => {
    if (!form.pan) return;
    setPanChecking(true);
    setPanResult(null);
    try {
      const { data } = await axios.post(`${API}/pan/verify`, { pan: form.pan, name: form.company_name || null });
      setPanResult({ ok: true, ...data });
    } catch (err) {
      setPanResult({ ok: false, message: errMsg(err, "Could not verify this PAN") });
    } finally {
      setPanChecking(false);
    }
  };

  // Feb 2026, user's explicit ask: AI-read bank details off an uploaded
  // cancelled cheque instead of typing them in - the fields stay fully
  // editable afterwards, and the cheque itself is also kept as a
  // supporting attachment. Never touches SAP (see cheque_reader_service.py).
  const readCheque = async (file) => {
    setReadingCheque(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/supplier-onboarding-requests/read-cheque`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const anyFieldRead = data.bank_account_number || data.bank_ifsc || data.bank_account_holder_name;
      setForm((f) => ({
        ...f,
        bank_account_number: data.bank_account_number || f.bank_account_number,
        bank_ifsc: data.bank_ifsc || f.bank_ifsc,
        bank_account_holder_name: data.bank_account_holder_name || f.bank_account_holder_name,
        bank_details_confirmed: false,
        bank_cheque_uploaded: true,
      }));
      // Feb 2026, user's explicit ask: "cheque upload is mandatory" -
      // the cheque file itself is kept as an attachment regardless of
      // whether AI managed to read any field off it; a requester can
      // still correct/type the fields by hand afterwards.
      addFiles([file]);
      if (data.warning && !anyFieldRead) {
        toast.error("Could not read cheque", { description: data.warning });
      } else if (data.warning) {
        toast.warning("Cheque partially read", { description: data.warning });
      } else {
        toast.success("Bank details read from cheque", { description: "Please double-check the fields before submitting." });
      }
    } catch (err) {
      toast.error("Could not read the cheque", { description: errMsg(err, "Please type the bank details in manually.") });
    } finally {
      setReadingCheque(false);
    }
  };

  const sendLink = async () => {
    setSendingLink(true);
    try {
      await axios.post(`${API}/supplier-onboarding-requests/initiate`, { supplier_email: supplierEmailInput });
      toast.dismiss();
      toast.success("Registration link sent", { description: `An email was sent to ${supplierEmailInput}` });
      setSendLinkOpen(false);
      setSupplierEmailInput("");
      loadMyRequests();
    } catch (err) {
      toast.dismiss();
      toast.error("Could not send the link", { description: errMsg(err) });
    } finally {
      setSendingLink(false);
    }
  };

  const verifyPin = async () => {
    setVerifyingPin(true);
    try {
      await axios.post(`${API}/supplier-onboarding-requests/${pinTarget.id}/verify-pin`, { pin: pinInput });
      toast.dismiss();
      toast.success("PIN confirmed - request is now with the admin for approval");
      setPinTarget(null);
      setPinInput("");
      loadMyRequests();
    } catch (err) {
      toast.dismiss();
      const message = errMsg(err);
      toast.error(message.toLowerCase().includes("too many") ? "PIN verification failed" : "Incorrect PIN", { description: message });
    } finally {
      setVerifyingPin(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setErrors({});
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v === null || v === undefined ? "" : v));
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/supplier-onboarding-requests`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.dismiss();
      toast.success("Request submitted for approval", {
        description: "An admin will review this before it's created in SAP.",
      });
      setForm(emptyForm);
      setFiles([]);
      setEmailOtp({ sentTo: null, verifiedValue: null, code: "", sending: false, verifying: false });
      navigate("/purchasing-strategy/supplier-master");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setErrors(detail);
        toast.error("Please fix the highlighted fields");
      } else {
        toast.error("Submission failed", { description: detail || err.message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (key) => (errors[key] || [])[0];
  const errCls = (key) => (fieldError(key) ? "border-[#B42318] focus:border-[#B42318] focus:ring-[#B42318]" : "");
  const bankFieldsFilled = !!(form.bank_account_holder_name && form.bank_account_number && form.bank_ifsc);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Request New Supplier</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-auto p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          <button
            type="button"
            onClick={() => navigate("/purchasing-strategy/supplier-master")}
            className="flex items-center gap-1.5 text-xs text-[#475467] hover:text-[#004B87]"
            data-testid="supplier-onboarding-back-button"
          >
            <ArrowLeft size={14} /> Back to Supplier Master
          </button>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Buildings size={18} weight="fill" className="text-[#004B87]" />
              <h1 className="font-heading text-base font-bold">Request New Supplier</h1>
            </div>
            <Button
              type="button"
              onClick={() => setSendLinkOpen(true)}
              className="h-8 bg-[#0E7C86] hover:bg-[#0B6670] text-white text-xs rounded-sm"
              data-testid="onboarding-send-link-button"
            >
              <PaperPlaneTilt size={13} className="mr-1.5" />
              Send Registration Link to Supplier
            </Button>
          </div>
          <p className="text-sm text-[#475467] -mt-2">
            Fill the form below yourself, or send the supplier a link so they fill it in themselves - either way
            the request needs the email PIN confirmed (for links) and admin approval before SAP creates it.
            Bank details are for the finance team's reference only - they will set those up in SAP separately.
          </p>

          <div className={sectionCls} data-testid="onboarding-my-requests-section">
            <div className={sectionTitleCls}>My Requests</div>
            {loadingRequests && <p className="text-xs text-[#98A2B3]">Loading...</p>}
            {!loadingRequests && myRequests.length === 0 && <p className="text-xs text-[#98A2B3]">No requests yet.</p>}
            {!loadingRequests && myRequests.length > 0 && (() => {
              const sorted = [...myRequests].sort(
                (a, b) => new Date(b.submitted_at || b.link_created_at || 0) - new Date(a.submitted_at || a.link_created_at || 0)
              );
              const visible = showAllRequests ? sorted : sorted.slice(0, 5);
              return (
                <>
                  <div className="space-y-1.5">
                    {visible.map((r) => (
                      <div key={r.id} className="flex items-center justify-between text-xs bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-2.5 py-1.5" data-testid={`onboarding-my-request-row-${r.id}`}>
                        <div>
                          <span className="font-medium text-[#1D2939]">{r.company_name || r.supplier_email}</span>
                          <Badge className="ml-2 bg-[#E3A008]/15 text-[#8A6116] rounded-sm">{STATUS_LABELS[r.status] || r.status}</Badge>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {r.attachments?.length > 0 && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setAttachmentsTarget(r)}
                              className="h-7 text-xs rounded-sm border-[#D0D5DD] text-[#475467]"
                              data-testid={`onboarding-view-documents-${r.id}`}
                            >
                              <FileText size={12} className="mr-1" /> Documents ({r.attachments.length})
                            </Button>
                          )}
                          {r.status === "awaiting_pin_verification" && (
                            <Button size="sm" onClick={() => setPinTarget(r)} className="h-7 bg-[#004B87] hover:bg-[#003A6A] text-white text-xs rounded-sm" data-testid={`onboarding-enter-pin-${r.id}`}>
                              Enter PIN
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {sorted.length > 5 && (
                    <button
                      type="button"
                      onClick={() => setShowAllRequests((v) => !v)}
                      className="text-xs text-[#004B87] hover:underline"
                      data-testid="onboarding-my-requests-toggle"
                    >
                      {showAllRequests ? "Show fewer" : `Show all (${sorted.length})`}
                    </button>
                  )}
                </>
              );
            })()}
          </div>

          <div className={sectionCls} data-testid="onboarding-company-section">
            <div className={sectionTitleCls}>Company Details</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Supplier ID (ERP Supplier ID) *</label>
                <input className={`${inputCls} ${errCls("supplier_id")}`} value={form.supplier_id} placeholder="ERP Supplier ID - e.g. A4173 or 555001, no spaces"
                  onChange={(e) => setField("supplier_id", e.target.value.replace(/\s+/g, "").toUpperCase())} data-testid="onboarding-supplier-id-input" />
                <p className="text-[11px] text-[#98A2B3] mt-1">This is the ERP Supplier ID written into SAP - a number 1-999999, or a code starting with a letter, up to 10 characters, no spaces</p>
                {fieldError("supplier_id") && <p className="text-xs text-[#B42318] mt-1">{fieldError("supplier_id")}</p>}
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Company / Supplier Name *</label>
                <input className={`${inputCls} ${errCls("company_name")}`} value={form.company_name}
                  onChange={(e) => { setTitleCaseField("company_name", e.target.value); setPanResult(null); }} data-testid="onboarding-company-name-input" />
                {fieldError("company_name") && <p className="text-xs text-[#B42318] mt-1">{fieldError("company_name")}</p>}
              </div>
              <div>
                <label className={labelCls}>Is GST Registered?</label>
                <select className={inputCls} value={form.is_gst_registered ? "yes" : "no"}
                  onChange={(e) => setField("is_gst_registered", e.target.value === "yes")} data-testid="onboarding-gst-registered-select">
                  <option value="yes">Yes</option>
                  <option value="no">No (unregistered / composition)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Category *</label>
                <select className={`${inputCls} ${errCls("category")}`} value={form.category}
                  onChange={(e) => setField("category", e.target.value)} data-testid="onboarding-category-select">
                  <option value="">Select category</option>
                  {SUPPLIER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {fieldError("category") && <p className="text-xs text-[#B42318] mt-1">{fieldError("category")}</p>}
              </div>
              <div className="col-span-2">
                <label className={labelCls}>GSTIN {form.is_gst_registered ? "*" : ""}</label>
                <div className="flex gap-2">
                  <input className={`${inputCls} ${errCls("gstin")}`} value={form.gstin} placeholder="15-character GSTIN"
                    onChange={(e) => { setField("gstin", e.target.value.toUpperCase()); setGstResult(null); }} data-testid="onboarding-gstin-input" />
                  <Button type="button" onClick={checkGstin} disabled={gstChecking || !form.gstin} className="h-9 shrink-0 bg-[#0E7C86] hover:bg-[#0B6670] text-white text-xs rounded-sm" data-testid="onboarding-verify-gstin-button">
                    <MagnifyingGlass size={13} className="mr-1" /> {gstChecking ? "Checking..." : "Verify"}
                  </Button>
                </div>
                {fieldError("gstin") && <p className="text-xs text-[#B42318] mt-1">{fieldError("gstin")}</p>}
                {gstResult && gstResult.ok && (
                  <p className="text-xs text-[#0B7A56] mt-1" data-testid="onboarding-gstin-result-ok">
                    {gstResult.legal_name} ({gstResult.trade_name}) - {gstResult.status} - {gstResult.address}
                  </p>
                )}
                {gstResult && !gstResult.ok && (
                  <p className="text-xs text-[#B42318] mt-1" data-testid="onboarding-gstin-result-fail">{gstResult.message}</p>
                )}
              </div>
              <div>
                <label className={labelCls}>PAN {!form.is_gst_registered ? "*" : ""}</label>
                <div className="flex gap-2">
                  <input className={`${inputCls} ${errCls("pan")}`} value={form.pan} placeholder="10-character PAN"
                    onChange={(e) => { setField("pan", e.target.value.toUpperCase()); setPanResult(null); }} data-testid="onboarding-pan-input" />
                  <Button type="button" onClick={checkPan} disabled={panChecking || !form.pan} className="h-9 shrink-0 bg-[#0E7C86] hover:bg-[#0B6670] text-white text-xs rounded-sm" data-testid="onboarding-verify-pan-button">
                    <MagnifyingGlass size={13} className="mr-1" /> {panChecking ? "Checking..." : "Verify"}
                  </Button>
                </div>
                {fieldError("pan") && <p className="text-xs text-[#B42318] mt-1">{fieldError("pan")}</p>}
                {panResult && panResult.ok && (
                  <p className={`text-xs mt-1 ${!panResult.verified ? "text-[#B42318]" : panResult.name_matched === false ? "text-[#B8860B]" : "text-[#0B7A56]"}`} data-testid="onboarding-pan-result-ok">
                    {panResult.verified ? `${panResult.name} - ${panResult.status}` : "Not found in the Income Tax database"}
                    {panResult.name_match_score != null && ` (name match: ${panResult.name_match_score}%${panResult.name_matched === false ? " - mismatch" : ""})`}
                  </p>
                )}
                {panResult && !panResult.ok && (
                  <p className="text-xs text-[#B42318] mt-1" data-testid="onboarding-pan-result-fail">{panResult.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className={sectionCls} data-testid="onboarding-contact-section">
            <div className={sectionTitleCls}>Primary Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Contact Name *</label>
                <input className={`${inputCls} ${errCls("contact_name")}`} value={form.contact_name}
                  onChange={(e) => setTitleCaseField("contact_name", e.target.value)} data-testid="onboarding-contact-name-input" />
                {fieldError("contact_name") && <p className="text-xs text-[#B42318] mt-1">{fieldError("contact_name")}</p>}
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <div className="flex gap-2">
                  <input className={`${inputCls} ${errCls("contact_email")}`} value={form.contact_email}
                    onChange={(e) => setField("contact_email", e.target.value)} data-testid="onboarding-contact-email-input" />
                  {emailVerified ? (
                    <Badge className="h-9 px-2 shrink-0 bg-[#ECFDF3] text-[#027A48] rounded-sm flex items-center gap-1" data-testid="onboarding-email-verified-badge">
                      <CheckCircle size={13} weight="fill" /> Verified
                    </Badge>
                  ) : (
                    <Button type="button" onClick={sendEmailOtp} disabled={emailOtp.sending || !form.contact_email}
                      className="h-9 shrink-0 bg-[#0E7C86] hover:bg-[#0B6670] text-white text-xs rounded-sm" data-testid="onboarding-send-email-otp-button">
                      {emailOtp.sending ? "Sending..." : emailOtp.sentTo === form.contact_email ? "Resend Code" : "Verify"}
                    </Button>
                  )}
                </div>
                {!emailVerified && emailOtp.sentTo === form.contact_email && (
                  <div className="flex gap-2 mt-1.5">
                    <input className={inputCls} value={emailOtp.code} placeholder="6-digit code"
                      onChange={(e) => setEmailOtp((s) => ({ ...s, code: e.target.value }))} data-testid="onboarding-email-otp-input" />
                    <Button type="button" onClick={confirmEmailOtp} disabled={emailOtp.verifying || !emailOtp.code}
                      className="h-9 shrink-0 bg-[#004B87] hover:bg-[#003A6A] text-white text-xs rounded-sm" data-testid="onboarding-confirm-email-otp-button">
                      {emailOtp.verifying ? "Checking..." : "Confirm"}
                    </Button>
                  </div>
                )}
                {fieldError("contact_email") && <p className="text-xs text-[#B42318] mt-1">{fieldError("contact_email")}</p>}
              </div>
              <div>
                <label className={labelCls}>Phone (10-digit mobile) *</label>
                <div className="flex gap-2">
                  <input className={`${inputCls} ${errCls("contact_phone")}`} value={form.contact_phone}
                    onChange={(e) => setField("contact_phone", e.target.value)} data-testid="onboarding-contact-phone-input" />
                  {phoneVerified ? (
                    <Badge className="h-9 px-2 shrink-0 bg-[#ECFDF3] text-[#027A48] rounded-sm flex items-center gap-1" data-testid="onboarding-phone-verified-badge">
                      <CheckCircle size={13} weight="fill" /> Verified
                    </Badge>
                  ) : (
                    <Button type="button" onClick={sendPhoneOtp} disabled={phoneOtp.sending || !form.contact_phone}
                      className="h-9 shrink-0 bg-[#0E7C86] hover:bg-[#0B6670] text-white text-xs rounded-sm" data-testid="onboarding-send-phone-otp-button">
                      {phoneOtp.sending ? "Sending..." : phoneOtp.sentTo === form.contact_phone ? "Resend Code" : "Verify"}
                    </Button>
                  )}
                </div>
                {!phoneVerified && phoneOtp.sentTo === form.contact_phone && (
                  <div className="flex gap-2 mt-1.5">
                    <input className={inputCls} value={phoneOtp.code} placeholder="6-digit WhatsApp code"
                      onChange={(e) => setPhoneOtp((s) => ({ ...s, code: e.target.value }))} data-testid="onboarding-phone-otp-input" />
                    <Button type="button" onClick={confirmPhoneOtp} disabled={phoneOtp.verifying || !phoneOtp.code}
                      className="h-9 shrink-0 bg-[#004B87] hover:bg-[#003A6A] text-white text-xs rounded-sm" data-testid="onboarding-confirm-phone-otp-button">
                      {phoneOtp.verifying ? "Checking..." : "Confirm"}
                    </Button>
                  </div>
                )}
                {fieldError("contact_phone") && <p className="text-xs text-[#B42318] mt-1">{fieldError("contact_phone")}</p>}
              </div>
              <div>
                <label className={labelCls}>Secondary Contact Name *</label>
                <input className={`${inputCls} ${errCls("secondary_contact_name")}`} value={form.secondary_contact_name}
                  onChange={(e) => setTitleCaseField("secondary_contact_name", e.target.value)} data-testid="onboarding-secondary-contact-name-input" />
                {fieldError("secondary_contact_name") && <p className="text-xs text-[#B42318] mt-1">{fieldError("secondary_contact_name")}</p>}
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Secondary Phone (10-digit mobile) *</label>
                <input className={`${inputCls} ${errCls("secondary_phone")}`} value={form.secondary_phone}
                  onChange={(e) => setField("secondary_phone", e.target.value)} data-testid="onboarding-secondary-phone-input" />
                {fieldError("secondary_phone") && <p className="text-xs text-[#B42318] mt-1">{fieldError("secondary_phone")}</p>}
              </div>
            </div>
          </div>

          <div className={sectionCls} data-testid="onboarding-address-section">
            <div className={sectionTitleCls}>Registered Address</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Address Line 1 *</label>
                <input className={`${inputCls} ${errCls("address_line")}`} value={form.address_line} maxLength={60}
                  onChange={(e) => setTitleCaseField("address_line", e.target.value)} data-testid="onboarding-address-line-input" />
                <p className="text-[11px] text-[#98A2B3] mt-1">{form.address_line.length}/60 characters - SAP's own limit, keep it short</p>
                {form.address_line.length >= 60 && (
                  <p className="text-[11px] text-[#B54708] mt-0.5" data-testid="onboarding-address-overflow-hint">
                    Ran out of space? Type the rest in Address Line 2 below
                  </p>
                )}
                {fieldError("address_line") && <p className="text-xs text-[#B42318] mt-1">{fieldError("address_line")}</p>}
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Address Line 2</label>
                <input className={`${inputCls} ${errCls("address_line_2")}`} value={form.address_line_2} maxLength={40}
                  onChange={(e) => setTitleCaseField("address_line_2", e.target.value)} data-testid="onboarding-address-line-2-input" />
                <p className="text-[11px] text-[#98A2B3] mt-1">{form.address_line_2.length}/40 characters - optional, SAP's own limit for this line</p>
                {fieldError("address_line_2") && <p className="text-xs text-[#B42318] mt-1">{fieldError("address_line_2")}</p>}
              </div>
              <div>
                <label className={labelCls}>City *</label>
                <input className={`${inputCls} ${errCls("city")}`} value={form.city}
                  onChange={(e) => setTitleCaseField("city", e.target.value)} data-testid="onboarding-city-input" />
                {fieldError("city") && <p className="text-xs text-[#B42318] mt-1">{fieldError("city")}</p>}
              </div>
              <div>
                <label className={labelCls}>State *</label>
                <input className={`${inputCls} ${errCls("state")}`} value={form.state}
                  onChange={(e) => setTitleCaseField("state", e.target.value)} data-testid="onboarding-state-input" />
                {fieldError("state") && <p className="text-xs text-[#B42318] mt-1">{fieldError("state")}</p>}
              </div>
              <div>
                <label className={labelCls}>PIN Code *</label>
                <input className={`${inputCls} ${errCls("pincode")}`} value={form.pincode}
                  onChange={(e) => setField("pincode", e.target.value)} data-testid="onboarding-pincode-input" />
                {fieldError("pincode") && <p className="text-xs text-[#B42318] mt-1">{fieldError("pincode")}</p>}
              </div>
            </div>
          </div>

          <div className={sectionCls} data-testid="onboarding-bank-section">
            <div className={sectionTitleCls}>Bank Details *</div>
            <label
              htmlFor="onboarding-cheque-input"
              className={`flex items-center justify-center gap-2 border border-dashed rounded-sm py-3 text-sm ${
                readingCheque
                  ? "border-[#D0D5DD] text-[#98A2B3] cursor-wait"
                  : form.bank_cheque_uploaded
                  ? "border-[#12B76A] text-[#12B76A] hover:bg-[#ECFDF3]"
                  : "border-[#004B87] text-[#004B87] cursor-pointer hover:bg-[#EFF8FF]"
              }`}
              data-testid="onboarding-cheque-dropzone"
            >
              {form.bank_cheque_uploaded ? <CheckCircle size={16} weight="fill" /> : <UploadSimple size={16} />}{" "}
              {readingCheque
                ? "Reading cheque..."
                : form.bank_cheque_uploaded
                ? "Cheque attached - click to replace"
                : "Upload a cancelled cheque * (reads the fields below automatically)"}
            </label>
            <input id="onboarding-cheque-input" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={readingCheque}
              onChange={(e) => { if (e.target.files[0]) readCheque(e.target.files[0]); e.target.value = ""; }}
              data-testid="onboarding-cheque-input" />
            {fieldError("bank_cheque_uploaded") && <p className="text-xs text-[#B42318]">{fieldError("bank_cheque_uploaded")}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Account Holder Name *</label>
                <input className={`${inputCls} ${errCls("bank_account_holder_name")}`} value={form.bank_account_holder_name}
                  onChange={(e) => setBankField("bank_account_holder_name", e.target.value)} data-testid="onboarding-bank-holder-input" />
                {fieldError("bank_account_holder_name") && <p className="text-xs text-[#B42318] mt-1">{fieldError("bank_account_holder_name")}</p>}
              </div>
              <div>
                <label className={labelCls}>Account Number *</label>
                <input className={`${inputCls} ${errCls("bank_account_number")}`} value={form.bank_account_number}
                  onChange={(e) => setBankField("bank_account_number", e.target.value)} data-testid="onboarding-bank-account-input" />
                {fieldError("bank_account_number") && <p className="text-xs text-[#B42318] mt-1">{fieldError("bank_account_number")}</p>}
              </div>
              <div>
                <label className={labelCls}>IFSC Code *</label>
                <input className={`${inputCls} ${errCls("bank_ifsc")}`} value={form.bank_ifsc}
                  onChange={(e) => setBankField("bank_ifsc", e.target.value.toUpperCase())} data-testid="onboarding-bank-ifsc-input" />
                {fieldError("bank_ifsc") && <p className="text-xs text-[#B42318] mt-1">{fieldError("bank_ifsc")}</p>}
              </div>
            </div>
            <label
              className={`flex items-start gap-2 text-sm pt-1 ${
                bankFieldsFilled && form.bank_cheque_uploaded ? "text-[#1D2939] cursor-pointer" : "text-[#98A2B3] cursor-not-allowed"
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                disabled={!bankFieldsFilled || !form.bank_cheque_uploaded}
                checked={form.bank_details_confirmed}
                onChange={(e) => setField("bank_details_confirmed", e.target.checked)}
                data-testid="onboarding-bank-confirm-checkbox"
              />
              I have reviewed these bank details and confirm they are correct (we cannot send them to SAP
              automatically yet, so this confirmation matters - a wrong account number or IFSC here means a
              failed or misdirected payment later).
            </label>
            {fieldError("bank_details_confirmed") && <p className="text-xs text-[#B42318] mt-1">{fieldError("bank_details_confirmed")}</p>}
          </div>

          <div className={sectionCls} data-testid="onboarding-attachments-section">
            <div className={sectionTitleCls}>Supporting Attachments</div>
            <label
              htmlFor="onboarding-file-input"
              className="flex items-center justify-center gap-2 border border-dashed border-[#D0D5DD] rounded-sm py-6 cursor-pointer text-[#475467] text-sm hover:border-[#004B87] hover:text-[#004B87]"
              data-testid="onboarding-file-dropzone"
            >
              <UploadSimple size={16} /> Click to attach GST certificate, PAN, cancelled cheque, etc.
            </label>
            <input id="onboarding-file-input" type="file" multiple className="hidden"
              onChange={(e) => addFiles(e.target.files)} data-testid="onboarding-file-input" />
            {files.length > 0 && (
              <div className="space-y-1.5">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-2.5 py-1.5" data-testid={`onboarding-attachment-row-${i}`}>
                    <span className="flex items-center gap-1.5 text-[#344054]"><FileText size={13} /> {f.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" onClick={() => viewFile(f)} className="text-[#0E7C86] hover:text-[#0B6670] flex items-center gap-1 font-semibold" data-testid={`onboarding-attachment-view-${i}`}>
                        <Eye size={14} /> View
                      </button>
                      <button type="button" onClick={() => removeFile(i)} className="text-[#98A2B3] hover:text-[#B42318]" data-testid={`onboarding-attachment-remove-${i}`}>
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button
            type="button"
            onClick={submit}
            disabled={submitting || !form.bank_details_confirmed || !form.bank_cheque_uploaded || !form.supplier_id || !emailVerified || !phoneVerified}
            className="h-10 w-full bg-[#004B87] hover:bg-[#003A6A] text-white text-sm rounded-sm"
            data-testid="onboarding-submit-button"
          >
            {submitting ? "Submitting..." : "Submit for Approval"}
          </Button>
          {!emailVerified && (
            <p className="text-xs text-[#98A2B3] text-center mt-1.5">Verify the contact email above before submitting</p>
          )}
          {emailVerified && !phoneVerified && (
            <p className="text-xs text-[#98A2B3] text-center mt-1.5">Verify the contact phone above before submitting</p>
          )}
        </div>
      </main>

      <Dialog open={sendLinkOpen} onOpenChange={setSendLinkOpen}>
        <DialogContent className="rounded-sm" data-testid="onboarding-send-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send Registration Link</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#475467]">
            The supplier will get an email with a link to fill in their own details, plus a PIN code they
            must read back to you before this request can go to admin approval.
          </p>
          <input
            type="email"
            className={inputCls}
            placeholder="supplier@example.com"
            value={supplierEmailInput}
            onChange={(e) => setSupplierEmailInput(e.target.value)}
            data-testid="onboarding-send-link-email-input"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setSendLinkOpen(false)}>Cancel</Button>
            <Button
              onClick={sendLink}
              disabled={sendingLink || !supplierEmailInput}
              className="rounded-sm bg-[#0E7C86] hover:bg-[#0B6670]"
              data-testid="onboarding-send-link-confirm-button"
            >
              {sendingLink ? "Sending..." : "Send Link"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinTarget} onOpenChange={(o) => !o && setPinTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="onboarding-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Enter PIN from {pinTarget?.supplier_email}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#475467]">Ask the supplier for the 6-digit PIN from their invitation email.</p>
          <input
            type="text"
            className={inputCls}
            placeholder="6-digit PIN"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            data-testid="onboarding-pin-input"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setPinTarget(null)}>Cancel</Button>
            <Button
              onClick={verifyPin}
              disabled={verifyingPin || !pinInput}
              className="rounded-sm bg-[#004B87] hover:bg-[#003A6A]"
              data-testid="onboarding-pin-confirm-button"
            >
              {verifyingPin ? "Verifying..." : "Confirm PIN"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!attachmentsTarget} onOpenChange={(o) => !o && setAttachmentsTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="onboarding-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Supporting Documents - {attachmentsTarget?.company_name || attachmentsTarget?.supplier_email}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            {(attachmentsTarget?.attachments || []).map((a, i) => (
              <a
                key={i}
                href={`${API}/supplier-onboarding-requests/${attachmentsTarget.id}/attachments/${encodeURIComponent(a.filename)}`}
                target="_blank" rel="noreferrer"
                className="text-[#0076CC] text-xs flex items-center gap-1.5 underline"
                data-testid={`onboarding-attachment-link-${i}`}
              >
                <FileText size={13} /> {a.filename} ({a.size < 1024 ? `${a.size} B` : `${(a.size / 1024).toFixed(1)} KB`})
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
